import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getEmails, syncEmailsNow } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { formatDate, getStatusColor, truncate } from '../../lib/utils';
import {
  Mail,
  MailOpen,
  Search,
  Filter,
  Paperclip,
  Star,
  RefreshCw,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';

export const EmailList = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadEmails();
  }, [statusFilter, searchTerm]);

  const loadEmails = async () => {
    setLoading(true);
    try {
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const search = searchTerm || undefined;
      const response = await getEmails(status, search, 300);
      setEmails(response.data);
    } catch (error) {
      console.error('Error loading emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncEmails = async () => {
    setSyncing(true);
    try {
      const response = await syncEmailsNow();
      if (response.data.success) {
        if (response.data.synced > 0 || response.data.deleted > 0) {
          toast.success(response.data.message);
        } else {
          toast.info(response.data.message || 'لا توجد تغييرات');
        }
        // Always reload emails after sync
        await loadEmails();
      } else {
        toast.error(response.data.message || 'فشل في المزامنة');
      }
    } catch (error) {
      console.error('Error syncing emails:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('انتهت مهلة المزامنة - حاول مرة أخرى');
      } else {
        toast.error('فشل في مزامنة البريد: ' + (error.response?.data?.detail || error.message || 'خطأ غير معروف'));
      }
    } finally {
      setSyncing(false);
    }
  };

  // Use debounced search - loadEmails is called when searchTerm changes
  const filteredEmails = emails; // Filtering is now done server-side

  const statusOptions = [
    { value: 'all', label: 'الكل', labelEn: 'All' },
    { value: 'new', label: 'جديد', labelEn: 'New' },
    { value: 'read', label: 'مقروء', labelEn: 'Read' },
    { value: 'in_progress', label: 'قيد المعالجة', labelEn: 'In Progress' },
    { value: 'awaiting_approval', label: 'بانتظار الموافقة', labelEn: 'Awaiting Approval' },
    { value: 'completed', label: 'مكتمل', labelEn: 'Completed' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
            صندوق الوارد
          </h1>
          <p className="text-muted-foreground mt-1">Inbox</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleSyncEmails}
            variant="default"
            className="rounded-sm"
            disabled={syncing}
            data-testid="sync-emails-btn"
          >
            <Download className={`h-4 w-4 mr-2 ${syncing ? 'animate-pulse' : ''}`} />
            {syncing ? 'جاري المزامنة...' : 'مزامنة من الخادم'}
          </Button>
          <Button
            onClick={loadEmails}
            variant="outline"
            className="rounded-sm border-border"
            disabled={loading}
            data-testid="refresh-emails-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="بحث في الرسائل..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-input/50 border-input rounded-sm"
                data-testid="email-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 rounded-sm" data-testid="status-filter-select">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="فلترة حسب الحالة" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label} ({option.labelEn})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Email List */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
            <Mail className="h-5 w-5" />
            الرسائل ({filteredEmails.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <MailOpen className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">لا توجد رسائل</p>
              <p className="text-sm">No emails found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredEmails.map((email) => (
                <Link
                  key={email.id}
                  to={`/emails/${email.id}`}
                  className="block"
                  data-testid={`email-row-${email.id}`}
                >
                  <div
                    className={`p-4 rounded-sm border transition-all hover:border-primary/40 ${
                      email.status === 'new'
                        ? 'bg-primary/5 border-primary/20'
                        : 'bg-muted/30 border-border'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div
                        className={`p-2 rounded-sm ${
                          email.status === 'new'
                            ? 'bg-primary/10'
                            : 'bg-muted/50'
                        }`}
                      >
                        {email.status === 'new' ? (
                          <Mail className="h-5 w-5 text-primary" />
                        ) : (
                          <MailOpen className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`font-medium ${
                              email.status === 'new' ? 'text-foreground' : ''
                            }`}
                          >
                            {email.sender_name || email.sender}
                          </span>
                          {email.is_quote_request && (
                            <Badge
                              variant="outline"
                              className="text-xs rounded-sm bg-green-500/10 text-green-500 border-green-500/20"
                            >
                              طلب عرض سعر
                            </Badge>
                          )}
                        </div>
                        <p
                          className={`text-sm mt-1 ${
                            email.status === 'new'
                              ? 'font-medium'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {email.subject}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                          {truncate(email.body, 100)}
                        </p>

                        {/* Meta */}
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="font-mono">
                            {formatDate(email.created_at)}
                          </span>
                          {email.attachments?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Paperclip className="h-3 w-3" />
                              {email.attachments.length}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="flex flex-col items-end gap-2">
                        <Badge
                          variant="outline"
                          className={`text-xs rounded-sm ${getStatusColor(
                            email.status
                          )}`}
                        >
                          {email.status}
                        </Badge>
                        {email.assigned_to && (
                          <span className="text-xs text-muted-foreground">
                            معين
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailList;
